// Écran 1 — Tableau de bord : pertes potentielles estimées par scénario, portes d'entrée, points uniques de défaillance,
// restaurabilité, avancement des travaux. Tout vient de l'API (moteur déterministe, carte, constats).
import { ReserveCalcul, ReserveCourte } from '../composants/ManquesCalcul';
import type { AttributsTravaux, Calcul, Carte, ObjetConstat, Objet, Variantes } from '../api/types';
import { Attendre, Chargement, Etiquette, EtiquetteGravite } from '../composants/communs';
import { BarresPertes } from '../composants/BarresPertes';
import { EtatVide } from '../composants/EtatVide';
import { PremiersPas } from '../composants/PremiersPas';
import { RapportDiagnostic } from '../composants/RapportDiagnostic';
import { PanneauVariantes } from '../composants/Variantes';
import { useCalcul, useCarte, useConstats, useVariantes } from '../contexte';
import { carteVide, indexer, nomDe } from '../domaine/carte';
import { eurosCourt, fourchette, pourcent } from '../domaine/format';
import { libelleNis2, portesEntree, scenarioDePanne, trierParPerte } from '../domaine/scenarios';
import { lienVers } from '../routeur';

export function EcranTableau() {
  const carte = useCarte();
  const calcul = useCalcul();
  const constats = useConstats();
  const variantes = useVariantes();
  // Un résultat gardé en cache ne décrit plus nécessairement la carte après une lecture ou une réponse : la carte
  // d'abord, puis, seulement pour une carte non vide, les calculs (sur une carte vide, l'API répond 409 aux calculs
  // et leur nouvelle tentative ne doit pas masquer « Premiers pas »).
  if (carte.isFetching) return <Chargement quoi="de la carte et des calculs" />;
  // Carte vide (première connexion) : le parcours « Premiers pas » et un état vide, sans attendre un calcul sans objet.
  if (carte.data && carteVide(carte.data))
    return (
      <div className="pile">
        <PremiersPas />
        <EtatVide
          titre="Le tableau de bord se remplira à la première lecture"
          idTitre="t-tableau-vide"
          texte={[
            'Il montrera les pertes potentielles estimées par scénario, les portes d’entrée, les points uniques de défaillance et l’avancement des travaux, tous calculés sur votre carte. Elle est vide pour l’instant.',
          ]}
        />
      </div>
    );
  if (calcul.isFetching || constats.isFetching) return <Chargement quoi="de la carte et des calculs" />;
  return (
    <Attendre etats={[carte, calcul, constats]} quoi="la carte et des calculs">
      {() => (
        // Les variantes ne bloquent pas le tableau : une API plus ancienne (sans la route) ou une carte sans
        // propositions affiche le reste sans elles.
        <>
          <PremiersPas />
          <Tableau carte={carte.data!} calcul={calcul.data!} constats={constats.data!.constats}
            variantes={variantes.isFetching || variantes.error ? undefined : variantes.data} />
        </>
      )}
    </Attendre>
  );
}

const ETATS_TRAVAUX = [
  { id: 'termine', libelle: 'Terminés', un: 'terminé', couleur: 'var(--ok)' },
  { id: 'en_cours', libelle: 'En cours', un: 'en cours', couleur: 'var(--navy-2)' },
  { id: 'valide', libelle: 'Validés', un: 'validé', couleur: 'var(--part)' },
  { id: 'propose', libelle: 'Proposés', un: 'proposé', couleur: 'var(--orange)' },
] as const;

function Tableau({
  carte,
  calcul,
  constats,
  variantes,
}: {
  carte: Carte;
  calcul: Calcul;
  constats: ObjetConstat[];
  variantes: Variantes | undefined;
}) {
  const ix = indexer(carte);
  const nom = (id: string) => nomDe(ix, id);
  const ind = calcul.indicateurs;
  const pertes = calcul.perte_annuelle_attendue;
  const pire = trierParPerte(calcul.scenarios)[0];
  const portes = portesEntree(calcul);
  const sauvegardes = ix.objets.filter((o) => o.type === 'sauvegarde');
  // Une variante de budget proposée par l'agent n'est pas un chantier (contrat : « Carte produite par l'agent »).
  const travaux = ix.objets.filter(
    (o): o is Objet & { type: 'travaux' } => o.type === 'travaux' && !('ekera_variante' in (o.extensions ?? {})),
  );
  const decisions = ix.objets.filter((o) => o.type === 'decision');
  const parEtat = (e: string) => travaux.filter((t) => ((t.attributs ?? {}) as AttributsTravaux).etat === e).length;
  const ouverts = constats.filter((c) => (c.attributs?.etat ?? 'ouvert') === 'ouvert');
  const nis2 = libelleNis2(calcul);
  return (
    <div className="pile">
      <p className="sous-titre">
        Ce que coûterait un arrêt, calculé sur vos comptes par le moteur déterministe d’Ekera, et ce qui reste à faire.
        Chaque chiffre mène à son détail.
      </p>
      <ReserveCalcul manques={calcul.manques} nom={nom} />
      <div className="kpis">
        <a
          className="kpi alerte"
          href={lienVers('constats')}
          title="Pertes potentielles estimées, conditionnelles aux hypothèses retenues. Calibration provisoire, non validée par un actuaire."
        >
          <ReserveCourte manques={calcul.manques} />
          <span className="l">Pertes potentielles estimées</span>
          <span className="v num">{pertes ? eurosCourt(pertes.haut.valeur) : '—'}</span>
          <span className="d">
            {pertes ? `fourchette ${fourchette(pertes.bas.valeur, pertes.haut.valeur)} par an, conditionnelles aux hypothèses retenues` : 'Montant non disponible'}
          </span>
        </a>
        <a
          className="kpi alerte"
          href={pire ? lienVers('constats', { scenario: pire.id }) : lienVers('constats')}
          title="Pertes potentielles estimées pour le scénario le plus lourd, conditionnelles aux hypothèses retenues. Calibration provisoire, non validée par un actuaire."
        >
          <ReserveCourte manques={calcul.manques} />
          <span className="l">Pire scénario</span>
          <span className="v num">{pire ? eurosCourt(pire.perte.haut.valeur) : '—'}</span>
          <span className="d">
            {pire
              ? `${pire.nature === 'attaque' ? 'attaque par' : 'panne de'} ${nom(pire.entree)} · fourchette ${fourchette(pire.perte.bas.valeur, pire.perte.haut.valeur)} par sinistre`
              : ''}
          </span>
        </a>
        <div className="kpi">
          <ReserveCourte manques={calcul.manques} />
          <span className="l">Arrêt maximal</span>
          <span className="v num">{pourcent(ind.rayon_max)}</span>
          <span className="d">de l’activité touchée</span>
        </div>
        <div className={`kpi ${ind.portes_entree ? 'alerte' : 'bien'}`}>
          <ReserveCourte manques={calcul.manques} />
          <span className="l">Portes d’entrée</span>
          <span className="v num">{ind.portes_entree}</span>
          <span className="d">exposées à une attaque</span>
        </div>
        <div className={`kpi ${ind.points_uniques_defaillance.length ? 'alerte' : 'bien'}`}>
          <ReserveCourte manques={calcul.manques} />
          <span className="l">Points uniques de défaillance</span>
          <span className="v num">{ind.points_uniques_defaillance.length}</span>
          <span className="d">sans secours</span>
        </div>
        <div
          className={`kpi ${nis2.complet ? 'bien' : 'alerte'}`}
          data-testid="kpi-nis2"
          title="Indicateur de progression sur les mesures évaluées, pas une certification de conformité."
        >
          <ReserveCourte manques={calcul.manques} />
          <span className="l">Indicateur de progression NIS2</span>
          <span className="v num">{nis2.score}</span>
          <span className="d">
            {nis2.detail} · pas une certification
          </span>
        </div>
      </div>
      <p className="note" data-testid="note-hypotheses">
        Les euros sont des <b>pertes potentielles estimées, conditionnelles aux hypothèses retenues</b> (calibration provisoire,
        non validée par un actuaire) ; les fréquences sont <b>estimées par le modèle, à calibrer selon le contexte</b>. L’indicateur NIS2
        est un <b>indicateur de progression sur les mesures évaluées, pas une certification de conformité</b>.
      </p>

      <section className="panneau" aria-labelledby="t-pertes">
        <div className="panneau-tete">
          <h2 id="t-pertes">Pertes potentielles estimées par scénario</h2>
          <span className="indice">Trait plein : valeur basse · plage claire : jusqu’à la valeur haute</span>
        </div>
        <ReserveCourte manques={calcul.manques} />
        <BarresPertes scenarios={trierParPerte(calcul.scenarios)} nom={nom} />
        <p className="note">
          Méthode : {calcul.methode === 'comptes' ? 'à partir de vos comptes (chiffre d’affaires, charges variables)' : calcul.methode}.
          Détail du calcul dans <a href={lienVers('constats')}>Constats et scénarios</a>.
        </p>
      </section>

      {variantes && <PanneauVariantes variantes={variantes} nom={nom} />}

      <div className="grille g3">
        <section className="panneau" aria-labelledby="t-portes" id="portes">
          <h2 id="t-portes">Portes d’entrée</h2>
          <ReserveCourte manques={calcul.manques} />
          <ul className="liste-points">
            {portes.map((p) => (
              <li key={p.scenario.id}>
                <a href={lienVers('constats', { scenario: p.scenario.id })}>
                  <b>{nom(p.entree)}</b>
                </a>
                <small>{p.motif}</small>
                <small className="num">
                  {fourchette(p.scenario.perte.bas.valeur, p.scenario.perte.haut.valeur)} · arrêt de {pourcent(p.scenario.rayon)}
                </small>
              </li>
            ))}
          </ul>
        </section>
        <section className="panneau" aria-labelledby="t-spof" id="spof">
          <h2 id="t-spof">Points uniques de défaillance</h2>
          <ReserveCourte manques={calcul.manques} />
          {ind.points_uniques_defaillance.length === 0 ? (
            <p className="encadre ok">Chaque infrastructure partagée a un secours.</p>
          ) : (
            <ul className="liste-points">
              {ind.points_uniques_defaillance.map((id) => {
                const s = scenarioDePanne(calcul, id);
                return (
                  <li key={id}>
                    <a href={lienVers('carte', { objet: id })}>
                      <b>{nom(id)}</b>
                    </a>
                    {s && (
                      <>
                        <small>Sa panne arrête {s.processus_arretes.length} processus.</small>
                        <small className="num">{fourchette(s.perte.bas.valeur, s.perte.haut.valeur)} par panne</small>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
        <section className="panneau" aria-labelledby="t-resto">
          <h2 id="t-resto">Restaurabilité</h2>
          <div className="ligne">
            {ind.restaurable ? <Etiquette ton="ok">Restaurable</Etiquette> : <Etiquette ton="grave">Non restaurable</Etiquette>}
            {ind.sauvegarde_hors_ligne ? (
              <Etiquette ton="ok">Sauvegarde hors ligne</Etiquette>
            ) : (
              <Etiquette ton="grave">Aucune sauvegarde hors ligne</Etiquette>
            )}
          </div>
          <p className="note">
            {ind.restaurable
              ? 'Après le pire scénario, les données peuvent être restaurées.'
              : 'Dans le pire scénario, l’attaquant atteint aussi les sauvegardes : les données seraient à reconstruire, non à restaurer.'}
          </p>
          <ul className="liste-points">
            {sauvegardes.map((s) => {
              const a = (s.attributs ?? {}) as Record<string, unknown>;
              return (
                <li key={s.id} className={a.hors_ligne ? 'ok' : ''}>
                  <a href={lienVers('carte', { objet: s.id })}>
                    <b>{s.nom}</b>
                  </a>
                  <small>
                    Hors ligne : {a.hors_ligne === true ? 'oui' : a.hors_ligne === false ? 'non' : 'inconnu'} · membre du domaine :{' '}
                    {a.membre_domaine === true ? 'oui' : a.membre_domaine === false ? 'non' : 'inconnu'}
                  </small>
                </li>
              );
            })}
          </ul>
        </section>
      </div>

      <section className="panneau" aria-labelledby="t-travaux">
        <div className="panneau-tete">
          <h2 id="t-travaux">Avancement des travaux</h2>
          <span className="indice">
            {travaux.length} chantier{travaux.length > 1 ? 's' : ''} · {decisions.length} décision{decisions.length > 1 ? 's' : ''} ·{' '}
            {ouverts.length} constat{ouverts.length > 1 ? 's' : ''} ouvert{ouverts.length > 1 ? 's' : ''}
          </span>
        </div>
        {travaux.length === 0 ? (
          <p className="encadre info">
            Aucun chantier n’est encore enregistré dans la carte. Proposez-en un depuis un constat, dans{' '}
            <a href={lienVers('corrections', { mode: 'travaux' })}>Corrections</a>.
          </p>
        ) : (
          <>
            <div className="avancement" role="img" aria-label={ETATS_TRAVAUX.map((e) => `${e.libelle} : ${parEtat(e.id)}`).join(', ')}>
              {ETATS_TRAVAUX.map((e) => (
                <i key={e.id} style={{ width: `${(parEtat(e.id) / travaux.length) * 100}%`, background: e.couleur }} />
              ))}
            </div>
            <div className="legende">
              {ETATS_TRAVAUX.map((e) => (
                <span key={e.id}>
                  <i style={{ background: e.couleur, borderColor: e.couleur }} />
                  {e.libelle} : {parEtat(e.id)}
                </span>
              ))}
            </div>
            <ul className="liste-points">
              {travaux.map((t) => {
                const a = (t.attributs ?? {}) as AttributsTravaux;
                return (
                  <li key={t.id} className={a.etat === 'termine' ? 'ok' : ''}>
                    <b>{t.nom}</b>
                    <small>
                      {ETATS_TRAVAUX.find((e) => e.id === a.etat)?.un ?? a.etat ?? 'état inconnu'}
                      {a.cout ? ` · ${eurosCourt(a.cout.valeur)}` : ''}
                      {a.delai_jours ? ` · ${a.delai_jours} jours` : ''}
                    </small>
                  </li>
                );
              })}
            </ul>
          </>
        )}
        <details>
          <summary>Constats ouverts aux pertes potentielles estimées les plus élevées</summary>
          <ul className="liste-points" style={{ marginTop: 10 }}>
            {[...ouverts]
              .sort((a, b) => (b.attributs?.perte_estimee?.haut.valeur ?? 0) - (a.attributs?.perte_estimee?.haut.valeur ?? 0))
              .slice(0, 6)
              .map((c) => (
                <li key={c.id}>
                  <a href={lienVers('constats', { constat: c.id })}>
                    <b>{c.nom}</b>
                  </a>
                  <small className="ligne">
                    <EtiquetteGravite g={c.attributs?.gravite} />
                    {c.attributs?.perte_estimee &&
                      fourchette(c.attributs.perte_estimee.bas.valeur, c.attributs.perte_estimee.haut.valeur)}
                  </small>
                </li>
              ))}
          </ul>
        </details>
      </section>
      <RapportDiagnostic compact />
    </div>
  );
}
